import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { useFormatting } from '@/admin/hooks/use-formatting'
import { z } from 'zod'
import {
  useWarrantyTemplates,
  useCreateWarrantyTemplate,
  useWarrantyCategories,
} from '@/admin/hooks/use-warranties'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/shared/components/ui/select'
import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { ConfigHelp } from '@/admin/components/config-help'
import { Plus, X } from 'lucide-react'
import { SearchInput } from '@/shared/components/ui/search-input'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { useForm } from '@/shared/lib/forms'
import { cn } from '@/shared/lib/utils'
import type {
  WarrantyType,
  WarrantyTemplateStatus,
  CoverageScope,
  WarrantyTemplate,
} from '@/admin/types/warranties'
import type { FormField } from '@/shared/lib/forms'

// ── Constants ─────────────────────────────────────────────

import { toastApiError } from '@/shared/lib/api-error'
const WARRANTY_TYPES: WarrantyType[] = [
  'MANUFACTURER_WARRANTY',
  'EXTENDED_WARRANTY',
  'RETAILER_WARRANTY',
]

type CreateTemplateValues = {
  name: string
  warranty_type: WarrantyType
  coverage_scope: CoverageScope | ''
  warranty_category_uuid: string
}

function useWarrantyTypeLabels(): Record<WarrantyType, string> {
  const { t } = useTranslation()
  return {
    MANUFACTURER_WARRANTY: t('warrantyClasses.manufacturer'),
    EXTENDED_WARRANTY: t('warrantyClasses.extended'),
    RETAILER_WARRANTY: t('warrantyClasses.retailer'),
  }
}

const COVERAGE_SCOPES: CoverageScope[] = ['MATERIAL_ONLY', 'MATERIAL_AND_LABOR']

const COVERAGE_LABELS: Record<string, string> = {
  MATERIAL_ONLY: 'Material Only',
  MATERIAL_AND_LABOR: 'Material & Labour',
}

function useCoverageScopeLabels(): Record<CoverageScope, string> {
  const { t } = useTranslation()
  return {
    MATERIAL_ONLY: t('warrantyClasses.materialOnly'),
    MATERIAL_AND_LABOR: t('warrantyClasses.materialAndLabour'),
  }
}

const STATUSES: WarrantyTemplateStatus[] = ['ACTIVE', 'DRAFT', 'ARCHIVED']

const STATUS_VARIANTS: Record<WarrantyTemplateStatus, 'default' | 'secondary' | 'outline'> = {
  ACTIVE: 'default',
  DRAFT: 'secondary',
  ARCHIVED: 'outline',
}

// ── Main Page Component ──────────────────────────────────

export function WarrantyClassesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { formatDate } = useFormatting()
  const WARRANTY_TYPE_LABELS = useWarrantyTypeLabels()
  const COVERAGE_SCOPE_LABELS = useCoverageScopeLabels()
  const { data: classes, isLoading } = useWarrantyTemplates()
  const { data: categories } = useWarrantyCategories()

  // Filters
  const [searchQuery, setSearchQuery] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<WarrantyTemplateStatus | ''>('')
  const [typeFilter, setTypeFilter] = useState<WarrantyType | ''>('')

  // Create dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const createClass = useCreateWarrantyTemplate()
  const createFields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'name',
        label: t('common.name'),
        placeholder: t('warrantyClasses.namePlaceholder'),
        value: '',
        className: 'col-span-1',
        validationSchema: z.string().min(1, 'Name is required'),
      },
      {
        type: 'select',
        name: 'warranty_type',
        label: t('warrantyClasses.warrantyType'),
        value: 'MANUFACTURER_WARRANTY',
        className: 'col-span-1',
        required: 'Warranty type is required',
        options: WARRANTY_TYPES.map((warrantyType) => ({
          label: WARRANTY_TYPE_LABELS[warrantyType],
          value: warrantyType,
        })),
      },
      {
        type: 'select',
        name: 'coverage_scope',
        label: t('warrantyClasses.coverageScope'),
        value: '__none__',
        className: 'col-span-1',
        options: [
          { label: t('common.none'), value: '__none__' },
          ...COVERAGE_SCOPES.map((coverageScope) => ({
            label: COVERAGE_SCOPE_LABELS[coverageScope],
            value: coverageScope,
          })),
        ],
      },
      {
        type: 'select',
        name: 'warranty_category_uuid',
        label: 'Category',
        value: '__none__',
        className: 'col-span-1',
        options: categories && categories.length > 0
          ? [
              { label: 'None', value: '__none__' },
              ...categories.map((category) => ({
                label: category.name,
                value: category.uuid,
              })),
            ]
          : [{ label: 'None', value: '__none__' }],
      },
    ],
    [COVERAGE_SCOPE_LABELS, WARRANTY_TYPE_LABELS, categories, t],
  )

  // Filter + sort the template list
  const filteredClasses = useMemo(() => {
    let result = (classes ?? []).slice()

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      result = result.filter(
        (wc) =>
          wc.name.toLowerCase().includes(q) ||
          (wc.description ?? '').toLowerCase().includes(q),
      )
    }

    if (categoryFilter === '__uncategorized__') {
      result = result.filter((wc) => !wc.warranty_category)
    } else if (categoryFilter) {
      result = result.filter((wc) => wc.warranty_category?.uuid === categoryFilter)
    }

    if (statusFilter) {
      result = result.filter((wc) => wc.status === statusFilter)
    }

    if (typeFilter) {
      result = result.filter((wc) => wc.warranty_type === typeFilter)
    }

    return result.sort((a, b) => a.name.localeCompare(b.name))
  }, [classes, searchQuery, categoryFilter, statusFilter, typeFilter])

  const hasActiveFilters =
    !!searchQuery.trim() || !!categoryFilter || !!statusFilter || !!typeFilter

  function clearFilters() {
    setSearchQuery('')
    setCategoryFilter('')
    setStatusFilter('')
    setTypeFilter('')
  }

  async function onCreateSubmit(values: CreateTemplateValues) {
    try {
      const payload: Record<string, unknown> = {
        name: values.name.trim(),
        warranty_type: values.warranty_type,
      }
      if (values.coverage_scope && values.coverage_scope !== '__none__') {
        payload.coverage_scope = values.coverage_scope
      }
      if (values.warranty_category_uuid && values.warranty_category_uuid !== '__none__') {
        payload.warranty_category_uuid = values.warranty_category_uuid
      }
      const created = await createClass.mutateAsync(payload as Partial<WarrantyTemplate>)
      setShowCreateDialog(false)
      toast.success(t('warrantyClasses.toasts.created'))
      navigate(`/manage/warranties/templates/${created.uuid}`)
    } catch (err) {
      toastApiError(err, t('warrantyClasses.toasts.createFailed'));
    }
  }

  const { Form: CreateTemplateForm, ref: createTemplateFormRef } = useForm(
    {
      name: 'create-warranty-template-form',
      submit: 'footer',
      fields: createFields,
    },
    async (rawValues) => {
      await onCreateSubmit({
        ...(rawValues as unknown as CreateTemplateValues),
        coverage_scope:
          (rawValues.coverage_scope as CoverageScope | '__none__' | '') === '__none__'
            ? ''
            : (rawValues.coverage_scope as CoverageScope | ''),
        warranty_category_uuid:
          rawValues.warranty_category_uuid === '__none__'
            ? ''
            : String(rawValues.warranty_category_uuid ?? ''),
      })
    },
    [showCreateDialog, categories, createFields],
  )

  // ── Table columns ────────────────────────────────────────
  const columns: DataTableColumn<WarrantyTemplate>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: (wc) => (
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <span className="text-[var(--ink)]">{wc.name}</span>
            {wc.policy_code && (
              <span className="rounded-[3px] bg-[var(--bg-subtle)] px-1.5 py-0.5 font-mono text-[9px] text-[var(--ink-3)]">
                {wc.policy_code}
              </span>
            )}
          </div>
          {wc.description && wc.description !== wc.name && (
            <span className="mt-0.5 truncate text-[11px] text-[var(--ink-3)]">
              {wc.description}
            </span>
          )}
        </div>
      ),
    },
    {
      id: 'category',
      header: 'Category',
      cell: (wc) =>
        wc.warranty_category ? (
          <Badge variant="outline" className="text-[10px]">
            {wc.warranty_category.name}
          </Badge>
        ) : (
          <span className="text-[11px] text-[var(--ink-3)]">Uncategorized</span>
        ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (wc) => (
        <Badge variant={STATUS_VARIANTS[wc.status]} className="text-[10px]">
          {wc.status.toLowerCase()}
        </Badge>
      ),
    },
    {
      id: 'type',
      header: 'Type',
      cell: (wc) => (
        <span className="text-[11px] text-[var(--ink-2)]">
          {WARRANTY_TYPE_LABELS[wc.warranty_type]}
        </span>
      ),
    },
    {
      id: 'coverage',
      header: 'Coverage',
      cell: (wc) => (
        <span className="text-[11px] text-[var(--ink-2)]">
          {wc.coverage_scope ? COVERAGE_LABELS[wc.coverage_scope] : '-'}
        </span>
      ),
    },
    {
      id: 'options',
      header: 'Options',
      cell: (wc) => (
        <span className="text-[11px] text-[var(--ink-2)]">
          {wc.options?.length ?? 0}
        </span>
      ),
    },
    {
      id: 'priority',
      header: 'Priority',
      cell: (wc) => (
        <span className="text-[11px] text-[var(--ink-2)]">{wc.priority}</span>
      ),
    },
    {
      id: 'active_from',
      header: 'Active From',
      cell: (wc) => (
        <span className="text-[11px] font-light text-[var(--ink-3)]">
          {wc.active_from ? formatDate(wc.active_from) : '-'}
        </span>
      ),
    },
    {
      id: 'active_until',
      header: 'Active Until',
      cell: (wc) => (
        <span className="text-[11px] font-light text-[var(--ink-3)]">
          {wc.active_until ? formatDate(wc.active_until) : '-'}
        </span>
      ),
    },
  ]

  return (
    <div className="py-6" data-testid="warranty-classes" data-tour="warranty-list">
      {/* Toolbar: title + new */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="clara-section-label">
            {t('warrantyClasses.title', 'Warranty Templates')}
            {hasActiveFilters && (
              <span className="ml-2 normal-case tracking-normal text-[var(--ink-3)]">
                ({filteredClasses.length} of {classes?.length ?? 0})
              </span>
            )}
          </span>
          <ConfigHelp
            sections={["display.warranty_table"]}
            returnLabel="Warranty Templates"
          />
        </div>
        <Button
          onClick={() => setShowCreateDialog(true)}
          data-testid="warranty-classes-add-btn"
          className="flex h-auto cursor-pointer items-center gap-1.5 rounded-[4px] bg-[var(--green)] px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] transition-all duration-150 hover:bg-[var(--green-hover)]"
        >
          <Plus className="h-3.5 w-3.5" />
          {t('warrantyClasses.newClass', 'New warranty template')}
        </Button>
      </div>
      {/* Filters row */}
      <div data-tour="warranty-types" className="mb-4 flex flex-wrap items-center gap-2">
        {/* Search */}
        <div className="flex-1 min-w-[240px]">
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search name or description..."
            data-testid="warranty-classes-search"
            iconSize={12}
            className="h-8 text-[12px]"
          />
        </div>

        {/* Category filter */}
        <Select
          value={categoryFilter || '__all__'}
          onValueChange={(v) => setCategoryFilter(v === '__all__' ? '' : v)}
        >
          <SelectTrigger size="sm" className="h-8 w-[160px] text-[11px]">
            <SelectValue>
              {categoryFilter === '__uncategorized__'
                ? 'Uncategorized'
                : categoryFilter
                  ? (categories ?? []).find((c) => c.uuid === categoryFilter)?.name
                  : 'All categories'}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All categories</SelectItem>
            <SelectItem value="__uncategorized__">Uncategorized</SelectItem>
            {(categories ?? []).map((cat) => (
              <SelectItem key={cat.uuid} value={cat.uuid}>
                {cat.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Status filter */}
        <Select
          value={statusFilter || '__all__'}
          onValueChange={(v) =>
            setStatusFilter(v === '__all__' ? '' : (v as WarrantyTemplateStatus))
          }
        >
          <SelectTrigger size="sm" className="h-8 w-[130px] text-[11px]">
            <SelectValue>
              {statusFilter
                ? statusFilter.charAt(0) + statusFilter.slice(1).toLowerCase()
                : 'All statuses'}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All statuses</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {s.charAt(0) + s.slice(1).toLowerCase()}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Type filter */}
        <Select
          value={typeFilter || '__all__'}
          onValueChange={(v) => setTypeFilter(v === '__all__' ? '' : (v as WarrantyType))}
        >
          <SelectTrigger size="sm" className="h-8 w-[160px] text-[11px]">
            <SelectValue>
              {typeFilter ? WARRANTY_TYPE_LABELS[typeFilter] : 'All types'}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All types</SelectItem>
            {WARRANTY_TYPES.map((wt) => (
              <SelectItem key={wt} value={wt}>
                {WARRANTY_TYPE_LABELS[wt]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {hasActiveFilters && (
          <Button
            variant="ghost"
            onClick={clearFilters}
            className={cn(
              "flex h-8 cursor-pointer items-center gap-1 px-2 text-[11px] text-[var(--ink-3)]",
              'hover:bg-[var(--bg-subtle)] hover:text-[var(--ink)]',
            )}
          >
            <X className="h-3 w-3" />
            Clear
          </Button>
        )}
      </div>
      {/* Table */}
      <DataTable<WarrantyTemplate>
        tableId="warranty-templates"
        columns={columns}
        data={filteredClasses}
        isLoading={isLoading}
        emptyMessage={
          (classes ?? []).length
            ? 'No templates match the current filters.'
            : (t('warrantyClasses.emptyState', 'No warranty templates yet.') as string)
        }
        onRowClick={(wc) => navigate(`/manage/warranties/templates/${wc.uuid}`)}
        rowKey={(wc) => wc.uuid}
        rowTestId={(wc) => `warranty-classes-item-${wc.uuid}`}
      />
      {/* Create dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {t('warrantyClasses.newClass', 'New warranty template')}
            </DialogTitle>
          </DialogHeader>
          <>
            <CreateTemplateForm className="grid grid-cols-1 gap-4 py-2" />
            <DialogFooter>
              <Button
                variant="outline"
                type="button"
                onClick={() => setShowCreateDialog(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="button"
                data-testid="warranty-classes-dialog-submit"
                disabled={createClass.isPending}
                onClick={() => createTemplateFormRef.current?.submitForm()}
              >
                {t('common.create')}
              </Button>
            </DialogFooter>
          </>
        </DialogContent>
      </Dialog>
    </div>
  );
}
